import { getResellerIdFromRedux } from '@utils/common';
import http from 'httpConfig/http';

const API_BASE = import.meta.env.VITE_APP_API_URL;

const CHUNK_SIZE = 5 * 1024 * 1024;

type InitRes = { uploadId: string; key: string; bucket?: string };
type PartUrlRes = { url: string };
type CompleteRes<T> = T;
export enum FileTypeEnum {
    IMAGE = 'IMAGE',
    VIDEO = 'VIDEO',
    GIF = 'GIF',
    OTHER = 'OTHER',
}

function getFileTypeEnum(file: File): FileTypeEnum {
    const mime = (file.type || '').toLowerCase();
    const ext = (file.name.split('.').pop() || '').toLowerCase();

    if (mime.startsWith('image/')) {
        if (mime === 'image/gif' || ext === 'gif') return FileTypeEnum.GIF;
        return FileTypeEnum.IMAGE;
    }

    if (mime.startsWith('video/')) return FileTypeEnum.VIDEO;

    // sometimes browsers give empty mime; fallback to ext checks
    if (ext === 'gif') return FileTypeEnum.GIF;
    if (['jpg', 'jpeg', 'png', 'webp', 'bmp', 'tiff', 'tif', 'heic', 'heif', 'avif', 'svg'].includes(ext))
        return FileTypeEnum.IMAGE;
    if (['mp4', 'mov', 'm4v', 'webm', 'mkv', 'avi', '3gp'].includes(ext)) return FileTypeEnum.VIDEO;

    return FileTypeEnum.OTHER;
}

export async function initSiteGalleryMultipart(params: {
    clientId: string;
    projectId: string;
    payload: {
        name: string;
        contentType: string;
        fileSizeInBytes: number;
        extension: string;
    };
    signal?: AbortSignal;
}): Promise<InitRes> {
    const { clientId, projectId, payload, signal } = params;

    const resellerId = getResellerIdFromRedux();

    const { data } = await http.post<InitRes>(
        `${API_BASE}/${resellerId}/clients/${clientId}/projects/${projectId}/site-gallery/files/multipart/init`,
        payload,
        { signal }
    );

    return data;
}

export async function getSiteGalleryPartUrl(params: {
    clientId: string;
    projectId: string;
    key: string;
    uploadId: string;
    partNumber: number;
    signal?: AbortSignal;
}): Promise<PartUrlRes> {
    const { clientId, projectId, key, uploadId, partNumber, signal } = params;

    const resellerId = getResellerIdFromRedux();

    const { data } = await http.post<PartUrlRes>(
        `${API_BASE}/${resellerId}/clients/${clientId}/projects/${projectId}/site-gallery/files/multipart/part-url`,
        { key, uploadId, partNumber },
        { signal }
    );

    return data;
}

export async function completeSiteGalleryMultipart<T>(params: {
    clientId: string;
    projectId: string;
    payload: {
        fileName: string;
        key: string;
        uploadId: string;
        parts: Array<{ ETag: string; PartNumber: number }>;
        fileSizeInBytes: string;
        contentType: string;
        type: FileTypeEnum;
    };
    signal?: AbortSignal;
}): Promise<T> {
    const { clientId, projectId, payload, signal } = params;

    const resellerId = getResellerIdFromRedux();

    const { data } = await http.post<T>(
        `${API_BASE}/${resellerId}/clients/${clientId}/projects/${projectId}/site-gallery/files/multipart/complete`,
        payload,
        { signal }
    );

    return data;
}

export async function abortSiteGalleryMultipart(params: {
    clientId: string;
    projectId: string;
    key: string;
    uploadId: string;
    signal?: AbortSignal;
}) {
    const { clientId, projectId, key, uploadId, signal } = params;

    const resellerId = getResellerIdFromRedux();

    await http.post(
        `${API_BASE}/${resellerId}/clients/${clientId}/projects/${projectId}/site-gallery/files/multipart/abort`,
        { key, uploadId },
        { signal }
    );
}

export async function uploadSingleFileMultipart<T>(params: {
    file: File;
    clientId: string;
    projectId: string;
    signal?: AbortSignal;
    onProgress?: (progress: number) => void; // 0..100 for this file
}): Promise<T> {
    const { file, clientId, projectId, signal, onProgress } = params;

    const dot = file.name.lastIndexOf('.');
    const extension = dot >= 0 ? file.name.substring(dot) : '';
    const name = dot >= 0 ? file.name.substring(0, dot) : file.name;

    const init = await initSiteGalleryMultipart({
        clientId,
        projectId,
        payload: {
            name,
            contentType: file.type,
            fileSizeInBytes: file.size,
            extension,
        },
        signal,
    });

    const { uploadId, key } = init;

    try {
        const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
        const parts: Array<{ ETag: string; PartNumber: number }> = [];

        for (let partNumber = 1; partNumber <= totalChunks; partNumber++) {
            if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');

            const start = (partNumber - 1) * CHUNK_SIZE;
            const end = Math.min(start + CHUNK_SIZE, file.size);
            const chunk = file.slice(start, end);

            const { url } = await getSiteGalleryPartUrl({
                clientId,
                projectId,
                key,
                uploadId,
                partNumber,
                signal,
            });

            // Signed URL PUT (S3/OCI) - fetch is fine here.
            const putRes = await fetch(url, {
                method: 'PUT',
                body: chunk,
                headers: { 'Content-Type': file.type },
                signal,
            });

            if (!putRes.ok) {
                throw new Error(`Part ${partNumber} upload failed: ${putRes.status} ${putRes.statusText}`);
            }

            // Needs CORS expose header: ETag
            const etagRaw = putRes.headers.get('ETag') || putRes.headers.get('etag');
            if (!etagRaw) {
                throw new Error(`ETag missing for part ${partNumber}. Check storage CORS expose headers.`);
            }

            parts.push({ ETag: etagRaw.replace(/"/g, ''), PartNumber: partNumber });

            onProgress?.(Math.round((partNumber / totalChunks) * 100));
        }

        const complete = await completeSiteGalleryMultipart<T>({
            clientId,
            projectId,
            payload: {
                fileName: file.name,
                key,
                uploadId,
                parts,
                fileSizeInBytes: file.size.toString(),
                contentType: file.type,
                type: getFileTypeEnum(file),
            },
            signal,
        });

        return complete;
    } catch (err) {
        // best-effort abort
        try {
            await abortSiteGalleryMultipart({ clientId, projectId, key, uploadId, signal });
        } catch {
            // ignore abort failure
        }
        throw err;
    }
}

export async function uploadMultipleFilesSequentially<T>(params: {
    files: File[];
    clientId: string;
    projectId: string;
    signal?: AbortSignal;
    onOverallProgress?: (progress: number) => void;
}): Promise<T[]> {
    const { files, clientId, projectId, signal, onOverallProgress } = params;

    const results: T[] = [];
    const total = files.length;

    for (let i = 0; i < total; i++) {
        const res = await uploadSingleFileMultipart<T>({
            file: files[i],
            clientId,
            projectId,
            signal,
            onProgress: (p) => {
                const overall = Math.round(((i + p / 100) / total) * 100);
                onOverallProgress?.(overall);
            },
        });

        results.push(res);
        onOverallProgress?.(Math.round(((i + 1) / total) * 100));
    }

    return results;
}
